import React, {useEffect, useState, useCallback} from 'react';
import PROCESSING from 'components/cmp_processing/cmp_processing';
import MESSAGE from "components/cmp_message/cmp_message";
import ICON from 'components/cmp_icon/cmp_icon'
import { ToggleButtonGroup, ToggleButton, Button, TextField, Input } from "react-aria-components";
import debounce from 'lodash.debounce';


import './cmp_table.css';


export default function TABLE({
                                var_data_settings,
                                set_data_settings,
                                onChange,
                                processing,
                                totalrows,
                                columns,
                                items,
                                onRowClick,
                                filterButtons,
                                renderCell,
                                defaultSelectedFilter = 'all'
                              }) {

  //  variable declarations ------------------------------------------------------------------------------------------

  const dataSettings = var_data_settings || {};
  const setDataSettings = set_data_settings;

  const currentPage = () => Math.floor((dataSettings.offset || 0) / (dataSettings.limit || 1)) + 1;
  const totalPages = () => Math.ceil((typeof totalrows === 'number' ? totalrows : 0) / (dataSettings.limit || 1));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let onSearchDebounce = useCallback(debounce(onSearchCallback, 500), []);
  const [search, setSearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(new Set([defaultSelectedFilter]));

  //  event functions ------------------------------------------------------------------------------------------------


  useEffect(() => {
    setSelectedFilter(new Set(['all']));
  }, [filterButtons]);

  function onPageChange(page) {
    if (processing !== 'processing') {
      onChange && onChange({...dataSettings, offset: (page - 1) * dataSettings.limit});
    }
  }

  function onRefresh() {
    onChange && onChange(dataSettings);
  }

  function onSearchCallback(settings) {
    onChange && onChange(settings);
  }

  function onSearch(value) {
    setSearch(value);
    let newFilter = {...dataSettings};
    newFilter.offset = 0;
    newFilter.search = value;
    setDataSettings(newFilter);
    onSearchDebounce(newFilter);
  }

  function onSort(sortby) {
    let newFilter = {...dataSettings};  // This spreads all existing params
    if (newFilter.sortby === sortby) {
      newFilter.sortorder = newFilter.sortorder === 'asc' ? 'desc' : 'asc';
    } else {
      newFilter.sortorder = 'asc';
    }
    newFilter.sortby = sortby;
    newFilter.offset = 0;  // Reset pagination, but keep status/provider/search
    setDataSettings(newFilter);
    onChange && onChange(newFilter);
  }

  function handleRowClick(item) {
    onRowClick && onRowClick(item);
  }


  const handleFilterChange = (value) => {
    const updatedSettings = { ...dataSettings };
    updatedSettings.offset = 0;  // Reset to page 1

    if (value) {
      const filter = filterButtons.find(btn => btn.value === value);
      if (filter) {
        updatedSettings[filter.param] = value;
      }
    } else {
      filterButtons.forEach(btn => {
        delete updatedSettings[btn.param];
      });
    }
    onChange && onChange(updatedSettings);
  };


  // RENDER COMPONENT ================================================================================================

  return (
    <>
      <div className='table__options'>

        <TextField
          value={search || ''}
          onChange={onSearch}
          aria-label="Search table"
        >
            <Input
              placeholder='Search'
              className='input__icon'
            />
            <ICON name='search' className='icon search-icon' alt='search icon'/>

        </TextField>


        {filterButtons && filterButtons.length > 0 && (
          <ToggleButtonGroup
            aria-label="Filter"
            selectionMode="single"
            selectedKeys={selectedFilter}  // Changed from defaultSelectedKeys
            onSelectionChange={(keys) => {
              setSelectedFilter(keys);
              const selectedId = [...keys][0] || null;
              handleFilterChange(selectedId === 'all' ? null : selectedId);
            }}
          >
            <ToggleButton id="all">All</ToggleButton>
            {filterButtons.map((btn) => (
              <ToggleButton key={btn.value} id={btn.value}>
                {btn.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <Button
          onPress={onRefresh}
          isDisabled={processing === 'processing'}
          aria-label="Refresh table"
          className='btn--refresh'
        >
          <ICON name='refresh' className='icon' alt='refresh icon'/>
        </Button>

      </div>

      <div className='table__wrapper'>
        <PROCESSING state={processing}/>

        {items && typeof totalrows === 'number' && totalrows > 0 && processing !== 'error' && (
          <table className={`data-table ${processing}`} role='table'>
            <thead>
            <tr role='row'>
              {columns.map((column) => (
                column.display !== 'NO' && (
                  <th
                    key={column.key}
                    style={{width: column.width}}
                    role='columnheader'
                    scope='col'
                  >
                    {column.sortby ? (
                      <Button
                        onPress={() => onSort(column.sortby)}
                        className='table__header-btn'
                        aria-sort={
                          dataSettings && dataSettings.sortby === column.sortby
                            ? (dataSettings.sortorder === 'asc' ? 'ascending' : 'descending')
                            : 'none'
                        }
                      >
                        {column.label}
                      </Button>
                    ) : (
                      <span>{column.label}</span>
                    )}
                  </th>
                )
              ))}
            </tr>
            </thead>
            <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                role='row'
                onClick={() => handleRowClick(item)}
                style={{cursor: onRowClick ? 'pointer' : 'default'}}
              >
                {columns.map((column) => (
                  column.display !== 'NO' && (
                    <td key={`${item.id}-${column.key}`} role='cell'>
                      {renderCell ? renderCell(item, column) : item[column.key]}
                    </td>
                  )
                ))}
              </tr>
            ))}
            </tbody>
          </table>
        )}
      </div>

      {items && typeof totalrows === 'number' && totalrows === 0 && processing === 'inactive' && (
        <MESSAGE type='information'>
          <ICON name='warning' className='message__icon' alt='warning icon'/>
          <div className='message__text__wrapper'>
            <div className='message__text__header text--base bold'>There is nothing to display</div>
            <div className='text--sm normal'>No records were retrieved from the database</div>
          </div>
        </MESSAGE>
      )}

      {processing === 'error' && (
        <MESSAGE type='warning'>
          <ICON name='warning' className='message__icon' alt='warning icon'/>
          <div className='message__text__wrapper'>
            <div className='message__text__header text--base bold'>Data error</div>
            <div className='text--sm normal'>We were unable to retrieve any records from the database</div>
          </div>
        </MESSAGE>
      )}

      <div className='table__controls'>
        {typeof totalrows === 'number' && totalrows > 15 && totalrows > dataSettings.limit && (
          <div className='pagination'>
            <Button
              onPress={() => onPageChange(currentPage() - 1)}
              isDisabled={currentPage() === 1 || processing === 'processing'}
              aria-label="Previous page"
            >
              Previous
            </Button>

            <span className='pagination__info'>
    Page {currentPage()} of {totalPages()}
  </span>

            <Button
              onPress={() => onPageChange(currentPage() + 1)}
              isDisabled={currentPage() === totalPages() || processing === 'processing'}
              aria-label="Next page"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </>
  );
}